import { useEffect, useRef, useState } from "react";
import { gifStopAfter, pictureFrameDelays } from "../gifFrames.ts";
import { imgProps } from "../imageMemory.ts";

/** What is known about one GIF or WebP file: its bytes, its type, the length of each frame, and (once decoded) its last frame. */
interface Known {
  blob: Blob;
  type: "image/gif" | "image/webp";
  delays: number[] | null;
  last?: Promise<HTMLCanvasElement | null>;
}
const known = new Map<string, Known>();
const KEPT = 8;
const FETCH_TIMEOUT = 10_000;

async function learn(url: string): Promise<Known> {
  const had = known.get(url);
  if (had) {
    known.delete(url);
    known.set(url, had); // newest last
    return had;
  }
  // A route that hangs must not leave an empty box: the signal also covers reading the body, and the caller then draws the plain picture.
  const res = await fetch(url, { signal: AbortSignal.timeout(FETCH_TIMEOUT) });
  if (!res.ok) throw new Error(String(res.status));
  const blob = await res.blob();
  const read = pictureFrameDelays(new Uint8Array(await blob.arrayBuffer()));
  const entry: Known = { blob, type: read?.type ?? "image/gif", delays: read?.delays ?? null };
  known.set(url, entry);
  while (known.size > KEPT) known.delete(known.keys().next().value!);
  return entry;
}

/** The last frame, composed, as a canvas; null when the browser cannot decode it. Decoded once per file. */
function lastFrame(entry: Known): Promise<HTMLCanvasElement | null> {
  entry.last ??= (async () => {
    if (!entry.delays || typeof ImageDecoder === "undefined") return null;
    const decoder = new ImageDecoder({ data: entry.blob.stream(), type: entry.type });
    try {
      const { image } = await decoder.decode({ frameIndex: entry.delays.length - 1 });
      const c = document.createElement("canvas");
      c.width = image.displayWidth;
      c.height = image.displayHeight;
      c.getContext("2d")!.drawImage(image, 0, 0);
      image.close();
      return c;
    } catch {
      return null;
    } finally {
      decoder.close();
    }
  })();
  return entry.last;
}

/** The picture as it shows now, as a canvas: the stand-in for the last frame in a browser that cannot decode it (Safari has no ImageDecoder). It may be a frame short of the last. */
function snapshot(img: HTMLImageElement | null): HTMLCanvasElement | null {
  if (!img || !img.complete || !img.naturalWidth) return null;
  const c = document.createElement("canvas");
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  c.getContext("2d")!.drawImage(img, 0, 0);
  return c;
}

/**
 * A GIF or animated WebP in the message list (an uploaded GIF file, #529, or a picker GIF in image format, #589), played `plays` times (the GIF plays setting, #529), then held on its last frame
 * as a still. A change of `replay` plays it again. The browser animates and loops a GIF file by itself, so the page
 * reads the file's frame delays (gifFrames.ts, GIF and WebP), plays it from a new blob URL (a new picture starts at its first frame),
 * and after the delays of `plays` plays it shows the last frame, decoded by ImageDecoder, in a canvas in place of the
 * animation (drawing the <img> would give the first frame). It plays while it is on screen and counts from zero each
 * time it comes back, as a video GIF does. The box is never empty: the picture on show stays until the next one has
 * loaded, and the file's delays and last frame are kept for the next play. A file it cannot read just loops. A browser
 * without ImageDecoder (Safari) draws the playing <img> into the canvas when the time is up, which can be a frame short
 * of the last one; so does a file ImageDecoder fails on.
 *
 * `fetchUrl`: where the bytes come from, when that is not `url`. A picker GIF lives on KLIPY's host, which the page's
 * `connect-src` does not allow and which sends no CORS headers we know of, so its bytes come through our own
 * /api/gif-file (the route that saves one). Then the box stays empty until they are in (`url` is not drawn beside
 * them, which would fetch the file twice); if they fail, `url` is drawn and loops, as before.
 */
export function CountedGif({ url, fetchUrl, width, height, alt, plays, replay, onStopped }: {
  url: string;
  fetchUrl?: string;
  width: number;
  height: number;
  alt: string;
  plays: number;
  replay: number;
  onStopped: (stopped: boolean) => void;
}) {
  const holder = useRef<HTMLSpanElement>(null);
  const shown = useRef<HTMLImageElement>(null);
  const timer = useRef(0);
  const [visible, setVisible] = useState(typeof IntersectionObserver === "undefined");
  const [src, setSrc] = useState<string | null>(null);
  const [still, setStill] = useState<HTMLCanvasElement | null>(null);
  // What the picture on show needs to start its clock: set with the src, read when the picture has loaded.
  const run = useRef<{ entry: Known; src: string } | null>(null);
  const stopped = useRef(onStopped);
  stopped.current = onStopped;

  useEffect(() => {
    const el = holder.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((entries) => setVisible(entries[entries.length - 1]!.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    let dead = false;
    let made: string | null = null;
    run.current = null;
    stopped.current(false);
    (async () => {
      try {
        const entry = await learn(fetchUrl ?? url);
        if (dead) return;
        made = URL.createObjectURL(entry.blob);
        if (entry.delays) void lastFrame(entry); // decodes while it plays
        // Loaded and decoded before it replaces what is on show.
        const next = new Image();
        next.src = made;
        await next.decode().catch(() => {});
        if (dead) return;
        run.current = { entry, src: made };
        setSrc(made);
        setStill(null);
      } catch {
        if (!dead) setSrc(url); // it loops, as it did before
      }
    })();
    return () => {
      dead = true;
      clearTimeout(timer.current);
      if (made) URL.revokeObjectURL(made);
    };
  }, [visible, url, fetchUrl, plays, replay]);

  // The picture has loaded and starts to play: the clock for its plays starts here.
  const started = (loaded: string) => {
    const r = run.current;
    if (!r || r.src !== loaded || !r.entry.delays) return;
    clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      void lastFrame(r.entry).then((decoded) => {
        if (run.current !== r) return;
        const c = decoded ?? snapshot(shown.current);
        if (c) setStill(c);
        stopped.current(!!c);
      });
    }, gifStopAfter(r.entry.delays, plays));
  };

  const paint = (c: HTMLCanvasElement | null) => {
    if (!c || !still) return;
    c.width = still.width;
    c.height = still.height;
    c.getContext("2d")!.drawImage(still, 0, 0);
  };

  return (
    <span ref={holder} style={{ display: "block", width: "100%", aspectRatio: `${width} / ${height}` }}>
      {still ? (
        <canvas ref={paint} role="img" aria-label={alt} />
      ) : src || !fetchUrl ? (
        <img ref={shown} src={src ?? url} width={width} height={height} alt={alt} onLoad={src ? () => started(src) : undefined} {...(src ? {} : imgProps(url))} />
      ) : null}
    </span>
  );
}
